import React, { useState, useEffect, useMemo } from 'react';
import { StaffLeave, Staff } from '../types';
import { leaveAPI, lookupAPI } from '../services/api';
import './LeaveCalendar.css';

interface LeaveCalendarProps {
  className?: string;
}

const LeaveCalendar: React.FC<LeaveCalendarProps> = ({ className = '' }) => {
  // Initialize with current date
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaves, setLeaves] = useState<StaffLeave[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; leaveId: number | null; leaveInfo: string }>({
    show: false,
    leaveId: null,
    leaveInfo: ''
  });
  const [newLeave, setNewLeave] = useState<Partial<StaffLeave>>({
    date: '',
    leave: 'weekly_leave',
    staff_id: 0,
    title: ''
  });

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days: Array<{
      date: Date;
      isCurrentMonth: boolean;
      isToday: boolean;
      leaves: StaffLeave[];
    }> = [];
    
    const currentDateObj = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === currentDateObj.toDateString();
      
      // Find leaves for this date - Fix date comparison
      const dateString = date.toISOString().split('T')[0];
      console.log(`Checking date: ${dateString} for calendar day ${date.getDate()}`);
      
      const dayLeaves = leaves.filter(leave => {
        console.log(`Comparing leave date: ${leave.date} with calendar date: ${dateString}`);
        // Try multiple date formats for comparison
        const leaveDate = new Date(leave.date);
        const calendarDate = new Date(dateString);
        
        // Compare as YYYY-MM-DD strings
        const leaveDateStr = leave.date;
        const calendarDateStr = dateString;
        
        // Also compare as Date objects (ignoring time)
        const leaveDateOnly = new Date(leaveDate.getFullYear(), leaveDate.getMonth(), leaveDate.getDate());
        const calendarDateOnly = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate());
        
        const stringMatch = leaveDateStr === calendarDateStr;
        const dateMatch = leaveDateOnly.getTime() === calendarDateOnly.getTime();
        
        console.log(`Leave date: ${leave.date}, Calendar date: ${dateString}`);
        console.log(`String match: ${stringMatch}, Date match: ${dateMatch}`);
        
        return stringMatch || dateMatch;
      });
      
      if (dayLeaves.length > 0) {
        console.log(`Found ${dayLeaves.length} leaves for ${dateString}:`, dayLeaves);
      }
      
      days.push({
        date,
        isCurrentMonth,
        isToday,
        leaves: dayLeaves
      });
      
      // Stop if we've gone past the current month and filled a complete week
      if (date > lastDay && days.length % 7 === 0) break;
    }
    
    return days;
  }, [currentDate, leaves]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load staff
        const staffData = await lookupAPI.getStaff();
        console.log('Loaded staff data:', staffData);
        setStaff(staffData);
        
        // Load leaves for the current month view
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        
        console.log('Loading leaves from:', startOfMonth.toISOString().split('T')[0], 'to:', endOfMonth.toISOString().split('T')[0]);
        
        const leavesData = await leaveAPI.getAll({
          date_from: startOfMonth.toISOString().split('T')[0],
          date_to: endOfMonth.toISOString().split('T')[0]
        });
        
        console.log('Loaded leaves data:', leavesData);
        setLeaves(leavesData);
      } catch (error) {
        console.error('Error loading calendar data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [currentDate]);

  // Handle leave form submission
  const handleLeaveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newLeave.date || !newLeave.staff_id || !newLeave.leave) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      await leaveAPI.create(newLeave as Omit<StaffLeave, 'id'>);
      
      // Reload leaves
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const leavesData = await leaveAPI.getAll({
        date_from: startOfMonth.toISOString().split('T')[0],
        date_to: endOfMonth.toISOString().split('T')[0]
      });
      
      setLeaves(leavesData);
      setShowLeaveForm(false);
      setNewLeave({
        date: '',
        leave: 'weekly_leave',
        staff_id: 0,
        title: ''
      });
    } catch (error) {
      console.error('Error creating leave:', error);
      alert('Error creating leave. Please try again.');
    }
  };

  // Show delete confirmation
  const showDeleteConfirm = (leaveId: number, leaveInfo: string) => {
    setDeleteConfirm({
      show: true,
      leaveId,
      leaveInfo
    });
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (deleteConfirm.leaveId) {
      try {
        await leaveAPI.delete(deleteConfirm.leaveId);
        
        // Reload leaves
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        
        const leavesData = await leaveAPI.getAll({
          date_from: startOfMonth.toISOString().split('T')[0],
          date_to: endOfMonth.toISOString().split('T')[0]
        });
        
        setLeaves(leavesData);
        setDeleteConfirm({ show: false, leaveId: null, leaveInfo: '' });
        alert('Leave record deleted successfully');
      } catch (error) {
        console.error('Error deleting leave:', error);
        alert('Error deleting leave. Please try again.');
      }
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirm({ show: false, leaveId: null, leaveInfo: '' });
  };

  // Get leave type color
  const getLeaveTypeColor = (leaveType: string) => {
    switch (leaveType) {
      case 'sick_leave':
        return '#ff6b6b';
      case 'vacation':
        return '#51cf66';
      case 'weekly_leave':
        return '#74c0fc';
      default:
        return '#868e96';
    }
  };

  // Get leave type label
  const getLeaveTypeLabel = (leaveType: string) => {
    switch (leaveType) {
      case 'sick_leave':
        return 'Sick Leave';
      case 'vacation':
        return 'Vacation';
      case 'weekly_leave':
        return 'Weekly Leave';
      default:
        return leaveType;
    }
  };

  if (loading) {
    return (
      <div className={`leave-calendar ${className}`}>
        <div className="loading">Loading calendar...</div>
      </div>
    );
  }

  // Debug: Show raw data
  console.log('Current leaves state:', leaves);
  console.log('Current staff state:', staff);
  console.log('Generated calendar days:', calendarDays);

  return (
    <div className={`leave-calendar ${className}`}>
      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button onClick={goToPreviousMonth} className="nav-btn">
            &lt;
          </button>
          <h2 className="month-year">
            {currentDate.toLocaleDateString('en-US', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </h2>
          <button onClick={goToNextMonth} className="nav-btn">
            &gt;
          </button>
        </div>
        <button onClick={goToToday} className="today-btn">
          Today
        </button>
        <button 
          onClick={() => setShowLeaveForm(true)} 
          className="add-leave-btn"
        >
          Add Leave
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {/* Day headers */}
        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday-header">{day}</div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="calendar-days">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${selectedDate?.toDateString() === day.date.toDateString() ? 'selected' : ''}`}
              onClick={() => setSelectedDate(day.date)}
            >
              <div className="day-number">{day.date.getDate()}</div>
              <div className="day-leaves">
                {day.leaves.map((leave, leaveIndex) => (
                  <div
                    key={leaveIndex}
                    className="leave-indicator"
                    style={{ backgroundColor: getLeaveTypeColor(leave.leave) }}
                    title={`${staff.find(s => s.id === leave.staff_id)?.staff_name || 'Unknown'} - ${getLeaveTypeLabel(leave.leave)}`}
                  >
                    <span className="leave-text">
                      {staff.find(s => s.id === leave.staff_id)?.staff_name || 'Unknown'}
                    </span>
                    <button
                      className="delete-leave-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const staffName = staff.find(s => s.id === leave.staff_id)?.staff_name || 'Unknown';
                        const leaveType = getLeaveTypeLabel(leave.leave);
                        showDeleteConfirm(leave.id, `${staffName} - ${leaveType}`);
                      }}
                      title="Delete leave record"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="selected-date-details">
          <h3>Leave Details for {selectedDate.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</h3>
          
          {(() => {
            const selectedDateString = selectedDate.toISOString().split('T')[0];
            const selectedDateLeaves = leaves.filter(leave => leave.date === selectedDateString);
            
            if (selectedDateLeaves.length === 0) {
              return <p>No leave records for this date.</p>;
            }
            
            return (
              <div className="leave-details-list">
                {selectedDateLeaves.map((leave, index) => (
                  <div key={index} className="leave-detail-item">
                    <div className="leave-info">
                      <span className="staff-name">
                        {staff.find(s => s.id === leave.staff_id)?.staff_name || 'Unknown Staff'}
                      </span>
                      <span className="leave-type">
                        {getLeaveTypeLabel(leave.leave)}
                      </span>
                      {leave.title && <span className="leave-title">: {leave.title}</span>}
                    </div>
                    <div className="leave-actions">
                      <button
                        className="delete-leave-btn-large"
                        onClick={() => {
                          const staffName = staff.find(s => s.id === leave.staff_id)?.staff_name || 'Unknown';
                          const leaveType = getLeaveTypeLabel(leave.leave);
                          showDeleteConfirm(leave.id, `${staffName} - ${leaveType}`);
                        }}
                        title="Delete this leave record"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Leave Form Modal */}
      {showLeaveForm && (
        <div className="modal-overlay" onClick={() => setShowLeaveForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Add Leave</h3>
            <form onSubmit={handleLeaveSubmit}>
              <div className="form-group">
                <label htmlFor="staff">Employee:</label>
                <select
                  id="staff"
                  value={newLeave.staff_id || ''}
                  onChange={(e) => setNewLeave(prev => ({ ...prev, staff_id: parseInt(e.target.value) }))}
                  required
                >
                  <option value="">Select Employee</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.staff_name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="date">Date:</label>
                <input
                  type="date"
                  id="date"
                  value={newLeave.date}
                  onChange={(e) => setNewLeave(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="leave-type">Leave Type:</label>
                <select
                  id="leave-type"
                  value={newLeave.leave}
                  onChange={(e) => setNewLeave(prev => ({ ...prev, leave: e.target.value as any }))}
                  required
                >
                  <option value="weekly_leave">Weekly Leave</option>
                  <option value="sick_leave">Sick Leave</option>
                  <option value="vacation">Vacation</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="title">Title (Optional):</label>
                <input
                  type="text"
                  id="title"
                  value={newLeave.title || ''}
                  onChange={(e) => setNewLeave(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Leave reason or notes"
                />
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowLeaveForm(false)}>
                  Cancel
                </button>
                <button type="submit">Add Leave</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this leave record?</p>
            <p className="leave-info-text">{deleteConfirm.leaveInfo}</p>
            <div className="form-actions">
              <button type="button" onClick={cancelDelete} className="cancel-btn">
                Cancel
              </button>
              <button onClick={confirmDelete} className="delete-btn">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveCalendar;
